import { useCachedResource } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useScoreContext } from "../context/ScoreContext";
import { judges } from "../local/db";

/**
 * #3418: the other judges of a resolved composite, in the order they were
 * added. A resolution cached before #3416 (primary + secondary only) carries no
 * `others`, so it reads as [secondary]. A null slot (a judge row that no
 * longer resolves) is kept so the signature gate fails closed on it.
 */
export function compositeOthers(resolved) {
  if (Array.isArray(resolved?.others)) return resolved.others;
  return resolved?.secondary ? [resolved.secondary] : [];
}

/**
 * card #3196 - the composite resolution (GET /judge/:id/resolve, #1824) of each
 * judge id, as a Map(id -> resolved). Cache-first like useShowJudges: painted
 * from the `judgeComposite:<id>` meta rows hydrate (#3195) and every earlier
 * visit wrote, revalidated from score-api when online. A failed resolve for one
 * id keeps that id's cached answer (and omits it when there is none, which the
 * signature gate treats as "not resolved yet": it fails closed, same as before).
 */
export function useJudgeComposites(judgeIds) {
  const api = useScoreApi();
  const { online } = useScoreContext();
  const idsKey = (judgeIds ?? []).join("|");
  return useCachedResource(
    async () => {
      const ids = judgeIds ?? [];
      if (ids.length === 0) return new Map();
      const entries = await Promise.all(ids.map(async (id) => [id, await judges.getComposite(id)]));
      const hit = entries.filter(([, resolved]) => resolved);
      return hit.length ? new Map(hit) : undefined;
    },
    async (signal) => {
      const ids = judgeIds ?? [];
      if (ids.length === 0) return new Map();
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const resolved = unwrap(await api.judges.resolveJudgeComposite(id, { signal }));
            if (resolved) await judges.putComposite(id, resolved);
            return [id, resolved];
          } catch {
            if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
            return [id, await judges.getComposite(id)];
          }
        }),
      );
      return new Map(entries.filter(([, resolved]) => resolved));
    },
    [api, idsKey],
    online,
  );
}
